// Asset versions + image preload queue shared by the game UI.
//
// The server sends a content hash per asset path ("cards/eq_x.webp" -> hash).
// Versioned URLs (?v=hash) are cached by the browser for a year, so a changed
// file gets a new URL and everything else stays cached. Unknown paths (new
// files the client has not heard about yet) fall back to plain URLs.

let assetVersions = {};

export function setAssetVersions(files) {
  if (files && typeof files === "object") assetVersions = files;
}

// True/false once the manifest has arrived; null while it is still unknown.
export function isKnownAsset(relPath) {
  if (!Object.keys(assetVersions).length) return null;
  return Object.prototype.hasOwnProperty.call(assetVersions, relPath);
}

export function assetVersionedUrl(root, relPath, { thumb = false } = {}) {
  if (!relPath) return null;
  const encoded = String(relPath)
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
  const base = thumb ? `${root}/thumbs` : root;
  const version = assetVersions[relPath];
  return version ? `${base}/${encoded}?v=${version}` : `${base}/${encoded}`;
}

const LOADED_URL_LIMIT = 2400;
const loadedUrls = new Set();
const requestedUrls = new Set();
const HIGH_CONCURRENCY = 6;
const LOW_CONCURRENCY = 2;
const highQueue = [];
const lowQueue = [];
let activeLoads = 0;
const listeners = new Map();

export function markImageUrlLoaded(url) {
  if (!url) return;
  loadedUrls.delete(url);
  loadedUrls.add(url);
  if (loadedUrls.size > LOADED_URL_LIMIT) {
    loadedUrls.delete(loadedUrls.values().next().value);
  }
}

export function isImageUrlLoaded(url) {
  return Boolean(url && loadedUrls.has(url));
}

function prefersReducedData() {
  if (typeof navigator === "undefined") return false;
  const connection = navigator.connection;
  return Boolean(
    connection &&
      (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || "")),
  );
}

function finishUrl(url, ok) {
  if (ok) markImageUrlLoaded(url);
  else requestedUrls.delete(url);
  const callbacks = listeners.get(url);
  listeners.delete(url);
  (callbacks || []).forEach((callback) => callback(ok));
}

function pumpQueue() {
  while (highQueue.length || lowQueue.length) {
    const fromHigh = highQueue.length > 0;
    const limit = fromHigh ? HIGH_CONCURRENCY : LOW_CONCURRENCY;
    if (activeLoads >= limit) return;
    const url = fromHigh ? highQueue.shift() : lowQueue.shift();
    activeLoads += 1;
    const img = new Image();
    img.decoding = "async";
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      activeLoads -= 1;
      finishUrl(url, ok);
      pumpQueue();
    };
    img.onload = () => {
      if (typeof img.decode === "function") {
        img.decode().then(
          () => done(true),
          () => done(img.naturalWidth > 0),
        );
      } else {
        done(img.naturalWidth > 0);
      }
    };
    img.onerror = () => done(false);
    img.src = url;
  }
}

// Queues each URL once per session. "high" loads jump ahead of "low" ones;
// "low" loads are skipped on data-saver / 2G connections. Resolves when every
// queued URL has finished (loaded or failed), reporting progress on the way.
export function preloadImageUrls(urls, { priority = "high", onProgress } = {}) {
  const unique = [...new Set((urls || []).filter(Boolean))];
  if (priority === "low" && prefersReducedData()) {
    return Promise.resolve();
  }
  const total = unique.length;
  let completed = 0;
  const report = () => {
    if (typeof onProgress === "function") onProgress(completed, total);
  };
  report();
  const all = Promise.all(
    unique.map(
      (url) =>
        new Promise((resolve) => {
          const settle = () => {
            completed += 1;
            report();
            resolve();
          };
          if (loadedUrls.has(url)) {
            settle();
            return;
          }
          const callbacks = listeners.get(url) || [];
          callbacks.push(settle);
          listeners.set(url, callbacks);
          if (requestedUrls.has(url)) {
            // Promote a waiting low-priority load when something needs it now.
            const lowIndex = lowQueue.indexOf(url);
            if (priority === "high" && lowIndex >= 0) {
              lowQueue.splice(lowIndex, 1);
              highQueue.push(url);
            }
            return;
          }
          requestedUrls.add(url);
          (priority === "high" ? highQueue : lowQueue).push(url);
        }),
    ),
  );
  pumpQueue();
  return all;
}

export function preloadImageUrl(url, priority = "high") {
  return preloadImageUrls([url], { priority });
}
